import React from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonToolbar, MenuItem, Glyphicon, DropdownButton, Row, Col, } from 'react-bootstrap';
import classnames from 'classnames';
import _ from 'underscore';
import { humanize, titleize } from 'underscore.string';
import SessionStore from 'stores/SessionStore';
import TagEditor from 'components/ui/TagEditor';
import Constants from 'constants/AdminConstants';
import CargomaticCard from 'components/v2/card';
import Checkbox from 'components/v2/forms/Checkbox';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import Multiselect from 'components/v2/multi-select';
import CargomaticInput from 'components/v2/input';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import { productTypes } from 'config/shipper-config-items';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { shipperEdiEvents } from '../../shipments/mappings';
import RenderSeriesDropdown from '../rate-series-dropdown';
const ENABLED_EVENTS = [
    'created',
    'order_accepted',
    'accepted',
    'eta_pickup',
    'in_transit',
    'arrived_at_pickup',
    'picked_up_shipment',
    'eta_deliver',
    'arrived_at_delivery',
    'delivered',
    'stop_window_change',
    'stop_window_confirmed',
    'invoice_created',
    'credit_memo_created',
    'export_order_equipment_added',
    'attempted_pickup',
    'attempted_delivery',
    'container_available',
    'stack_position_updated',
    'container_discharged',
    'shipper_accessorial_pending_admin',
    'shipper_accessorial_pending_shipper',
    'shipper_accessorial_accepted',
    'shipper_accessorial_approved',
    'shipper_accessorial_declined',
    'arriving_at_delivery',
    'arriving_at_pickup',
    'bobtail_accessorial_created',
    'stopoff_accessorial_created',
    'prepull_accessorial_created',
    'rail_loaded',
    'rail_departed',
    'rail_arrived_at_facility',
    'rail_unloaded',
    'rail_available_at_facility',
    'rail_outgated_from_facility',
    'rail_lfd_at_facility',
    'rail_eta_final_facility',
    'rail_ingated_at_origin_facility',
    'rail_loaded_at_origin',
    'rail_departed_from_origin',
    'rail_arrived_at_destination_port_facility',
    'rail_unloaded_at_destination_port',
    'estimated_arrival',
    'last_free_date',
    'empty_ready_for_pickup',
    'arrival_date',
    'outgated_date',
    'empty_returned_date',
    'per_diem_lfd',
    'proof_of_delivery_uploaded',
    'earliest_empty_date',
    'document_cutoff_date',
    'last_cut_date',
    'empty_rail_billing',
    'loaded_rail_billing',
    'rail_earliest_return',
    'rail_cut_date',
    'requested_pickup_date',
    'requested_delivery_appointment',
    'must_arrive_by_date',
    'on_hold',
    'chassis_in_gate',
    'chassis_out_gate',
    'pickup_from_exam_site',
    'dropoff_at_exam_site',
];
const statuses = shipperEdiEvents.map((status) => status.slug);
const items = shipperEdiEvents.map((event) => (React.createElement("option", { key: event.slug, value: event.slug }, event.message)));
class EDISettings extends React.Component {
    constructor(props) {
        super(props);
        this.getInitState = (props) => {
            const { shipper } = props;
            const customerEvents = shipper.customer_configurations &&
                shipper.customer_configurations.enabled_events
                ? shipper.customer_configurations.enabled_events
                : {};
            const customerMapping = shipper.customer_configurations &&
                shipper.customer_configurations.status_mappings
                ? shipper.customer_configurations.status_mappings
                : {};
            _.map(customerMapping, (value, key) => {
                customerMapping[key] = {
                    value: value,
                    error: null,
                };
            });
            return {
                scac_code: shipper.scac_code,
                edi_version: shipper.edi_version,
                location_name: shipper.location_name,
                tags: shipper.tags,
                mappings: customerMapping,
                events: customerEvents,
                disabled: false,
                availableStatuses: statuses,
                tradelens_enabled: shipper.tradelens_enabled,
                menuItems: items,
                canEdit: SessionStore.getUserRoles().some((role) => ['admin', 'integration_manager'].includes(role)),
                selectedMarket: shipper.market || null,
                selectedSeriesId: shipper.series_id || null,
                productType: shipper.product_type || null,
                customerAccountNumber: shipper.customer_account_number,
                overrides: shipper.edi_overrides || [],
                override_input: { scac_code: '', enabled_events: [] },
                container_events: shipper.container_edi_events || [],
            };
        };
        this.handleTagUpdate = (tags) => this.setState({ tags });
        this.isValid = () => {
            if (_.filter(this.state.mappings, (value) => {
                return value.error === true;
            }).length > 0)
                return false;
            if (this.state.edi_version === 2 && !this.state.scac_code)
                return false;
            return true;
        };
        this.updateStatusMapping = (key, value) => {
            this.setState((prevState) => {
                const mappings = Object.assign(Object.assign({}, prevState.mappings), { [key]: {
                        value,
                        error: null,
                    } });
                return {
                    mappings,
                };
            });
        };
        this.handleMappingChange = (old, newKey) => {
            this.setState((prevState) => {
                const mappings = Object.assign({}, prevState.mappings);
                const error = mappings[old].value ? null : true;
                mappings[newKey] = {
                    value: mappings[old].value,
                    error: error,
                };
                delete mappings[old];
                return { mappings };
            });
        };
        this.renderMappings = () => {
            const mappings = [];
            const { t } = this.props;
            _.map(this.state.mappings, (value, key) => {
                const inputClass = classnames('form-field', 'block', 'w-full', 'mb-4', {
                    error: value.error,
                });
                mappings.push(React.createElement("div", { key: key, className: "mb-2 grid grid-cols-12 gap-4" },
                    React.createElement("div", { className: "col-span-5" },
                        React.createElement("label", { className: "block mb-2 form-label" }, t('STATUS')),
                        React.createElement("select", { className: "mb-4 form-select", defaultValue: key, onChange: (evt) => {
                                this.handleMappingChange(key, evt.target.value);
                            } }, this.state.menuItems)),
                    React.createElement("div", { className: "col-span-5" },
                        React.createElement("label", { className: "block mb-2 form-label" }, t('VALUE')),
                        React.createElement("input", { className: inputClass, type: "text", value: value.value, onChange: (evt) => {
                                this.updateStatusMapping(key, evt.target.value);
                            } })),
                    React.createElement("div", { className: "mt-6 col-span-2" },
                        React.createElement(DeleteButton, { onClick: () => this.removeStatus(key) }))));
            });
            return mappings;
        };
        // handleContainerEventSelect = (payload) => {
        //   const container_events = payload.map((eq) => eq.name);
        //   this.setState({ container_events });
        // };
        this.handleOverrideSelect = (items) => {
            const { override_input } = this.state;
            override_input.enabled_events = items;
            this.setState({ override_input });
        };
        this.removeOverride = (index) => {
            this.setState((prevState) => {
                const overrides = [...prevState.overrides];
                overrides.splice(index, 1);
                return { overrides };
            });
        };
        this.handleScacOverrideInput = (value) => {
            const { override_input } = this.state;
            override_input.scac_code = value;
            this.setState({ override_input });
        };
        this.handleAddOverrideRow = () => {
            const overrides = [...this.state.overrides];
            const { override_input } = this.state;
            const { scac_code, enabled_events } = override_input;
            const blank = {};
            enabled_events.forEach((ev) => {
                blank[ev.slug] = true;
            });
            const update = [{ scac_code, enabled_events: blank }];
            const newState = overrides.concat(update);
            this.setState({
                overrides: newState,
                override_input: { scac_code: '', enabled_events: [] },
            });
        };
        this.handleLocationNameChange = (location_name) => {
            this.setState({ location_name });
        };
        this.handleScacCodeChange = (scac_code) => {
            this.setState({ scac_code });
        };
        this.handleEdiVersionSelect = (edi_version) => {
            this.setState({ edi_version });
        };
        this.handleAddStatusMap = () => {
            this.setState((prevState) => {
                const mappings = Object.assign({}, prevState.mappings);
                const id = Object.keys(mappings).length;
                const nextStatus = this.state.availableStatuses[id];
                mappings[nextStatus] = { value: '', error: true };
                return { mappings };
            });
        };
        this.handleAddEvent = (status) => (checked) => {
            this.setState((prevState) => {
                const events = Object.assign({}, prevState.events);
                events[status] = checked;
                this.setState({ events });
            });
        };
        this.handleClearMappings = () => {
            this.setState((prevState) => {
                const mappings = Object.assign({}, prevState.mappings);
                _.map(mappings, (_value, key) => {
                    mappings[key] = { value: '', error: true };
                });
                this.setState({ mappings });
            });
        };
        this.renderEvents = () => {
            const { t } = this.props;
            return shipperEdiEvents.map((event) => {
                const { slug: status } = event;
                const checked = !!this.state.events[status];
                let disabled = !ENABLED_EVENTS.includes(status);
                if (status === 'accepted' && !disabled) {
                    const orderAcceptedChecked = this.state.events.order_accepted;
                    if (orderAcceptedChecked) {
                        disabled = true;
                    }
                }
                else if (status === 'order_accepted' && !disabled) {
                    const acceptedChecked = this.state.events.accepted;
                    if (acceptedChecked) {
                        disabled = true;
                    }
                }
                return [
                    React.createElement("div", { key: status, className: "col-span-1" },
                        React.createElement(Checkbox, { id: status, name: status, disabled: disabled, checked: checked, onChange: this.handleAddEvent(status) }, t(event.localizationSlug))),
                ];
            });
        };
        this.saveCustomer = () => {
            const mappings = {};
            _.map(this.state.mappings, (value, key) => {
                mappings[key] = value.value;
            });
            const update = {
                edi_version: this.state.edi_version,
                scac_code: this.state.scac_code,
                tags: this.state.tags,
                location_name: (this.state.location_name || '').trim(),
                customer_configurations: {
                    enabled_events: this.state.events,
                    status_mappings: mappings,
                },
                tradelens_enabled: this.state.tradelens_enabled,
                product_type: this.state.productType,
                customer_account_number: this.state.customerAccountNumber,
                edi_overrides: this.state.overrides,
                container_edi_events: this.state.container_events,
                series_id: this.state.selectedSeriesId,
            };
            if (this.state.selectedMarket) {
                const hasMarket = this.state.selectedMarket !== 'None';
                update.market = hasMarket ? this.state.selectedMarket : '';
                update.searchable_by_market = hasMarket;
            }
            const shipper = _.extend({ _id: this.props.shipper._id }, update);
            this.props.onCustomerPreferencesChange(shipper);
        };
        this.handleTradelensSelect = (key) => {
            const tradelens_enabled = key === 'true';
            this.setState({ tradelens_enabled });
        };
        this.handleMarketSelect = (key) => {
            this.setState({ selectedMarket: key });
        };
        this.handleSeriesSelect = (key) => {
            this.setState({ selectedSeriesId: key });
        };
        this.handleProductTypeSelect = (key) => {
            this.setState({ productType: key === '(None)' ? '' : key });
        };
        this.handleCustomerAccountInput = (customerAccountNumber) => {
            this.setState({ customerAccountNumber });
        };
        this.state = this.getInitState(props);
    }
    componentDidMount() { }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!_.isEqual(this.props, nextProps)) {
            this.setState(this.getInitState(nextProps));
        }
    }
    componentWillUnmount() { }
    renderEDIOverrides() {
        const { overrides } = this.state;
        const { t } = this.props;
        if (!overrides.length) {
            return React.createElement("span", { className: "ml-8 text-grey-darker" });
        }
        return this.state.overrides.map((over, index) => {
            const events = Object.keys(over.enabled_events).map((ev, i) => {
                return (React.createElement("span", { key: i, className: "text-grey-darker text-sm" }, `${humanize(ev)}, `));
            });
            return (React.createElement("div", { key: index, className: "p-4" },
                React.createElement("div", { className: "bg-white p-4 rounded shadow" },
                    React.createElement("div", { className: "flex justify-between" },
                        React.createElement("h4", { className: "text-grey-darker text-base", style: { display: 'inline-block' } },
                            t('EDI_OVERRIDE_SCAC'),
                            ": ",
                            over.scac_code,
                            ' '),
                        React.createElement(DeclineButton, { onClick: () => this.removeOverride(index) })),
                    React.createElement("div", null, events))));
        });
    }
    // renderContainerEvents() {
    //   const { container_events } = this.state;
    //   const containerEventMenuItems = containerEvents.map((ev) => {
    //     return {
    //       name: ev,
    //       slug: ev,
    //       selected: container_events.indexOf(ev) > -1,
    //     };
    //   });
    //   return (
    //     <div className="w-96">
    //       <Multiselect
    //         className="w-96"
    //         label="Container Events"
    //         items={containerEventMenuItems}
    //         onSelect={this.handleContainerEventSelect}
    //       />
    //     </div>
    //   );
    // }
    eventSelected(slug) {
        const { override_input } = this.state;
        const slugs = override_input.enabled_events.map((ev) => ev.slug);
        if (slugs.indexOf(slug) > -1) {
            return true;
        }
        return false;
    }
    renderEDIOverrideInput() {
        const { override_input } = this.state;
        const { t } = this.props;
        const eventMenuItems = shipperEdiEvents.map((status) => {
            status.name = t(status.localizationSlug);
            status.selected = this.eventSelected(status.slug);
            return status;
        });
        return (React.createElement("div", { className: "mb-4" },
            React.createElement(Row, null,
                React.createElement(Col, { xs: 5 },
                    React.createElement(CargomaticInput, { type: "text", placeholder: t('CUSTOMER_SCAC_CODE'), value: override_input.scac_code, onChange: (value) => this.handleScacOverrideInput(value), label: t('SCAC_CODE') })),
                React.createElement(Col, { xs: 5, className: "pb-2", style: { paddingTop: '25px' } },
                    React.createElement(Multiselect, { label: t('ENABLE_EVENTS'), items: eventMenuItems, onSelect: this.handleOverrideSelect })))));
    }
    removeStatus(key) {
        this.setState((prevState) => {
            const mappings = Object.assign({}, prevState.mappings);
            delete mappings[key];
            return {
                mappings,
            };
        });
    }
    renderMarkets() {
        if (!this.props.markets || !this.props.markets.length)
            return null;
        const { t } = this.props;
        const markets = this.props.markets
            .sort((a, b) => a.name.localeCompare(b.name))
            .concat({ code: 'None', name: '' })
            .map((market, i) => (React.createElement(MenuItem, { key: i, eventKey: market.code }, `${market.name} (${market.code})`)));
        return (React.createElement("div", { className: "mb-4" },
            React.createElement("label", { className: "block mb-1 form-label" }, t('MARKET', { count: 1 })),
            React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('SHIPPER_OPERATING_MARKET')),
            React.createElement(DropdownButton, { title: this.state.selectedMarket
                    ? this.state.selectedMarket
                    : 'Select Market', onSelect: this.handleMarketSelect }, markets)));
    }
    render() {
        const { t } = this.props;
        const disabled = Object.keys(this.state.mappings).length >=
            this.state.availableStatuses.length;
        const events = this.renderEvents();
        const mappings = this.renderMappings();
        const displayClear = Object.keys(this.state.mappings).length > 0 ? 'block' : 'none';
        const productTypeMenuItems = productTypes.map((type) => {
            return (React.createElement(MenuItem, { key: type, eventKey: type }, t(sluggify(type))));
        });
        productTypeMenuItems.push(React.createElement(MenuItem, { key: "none", eventKey: "(None)" },
            "(",
            t('NONE'),
            ")"));
        return (React.createElement(CargomaticCard, { header: () => React.createElement("h3", { className: "my-0" }, t('INTEGRATION_SETTINGS')), footer: () => (React.createElement("div", { className: "form-actions" },
                React.createElement("button", { className: "button button-green", disabled: !this.state.canEdit || !this.isValid(), onClick: () => this.saveCustomer() }, t('SAVE')))), grayFooter: true },
            React.createElement("div", { className: "mb-4 lg:grid lg:grid-cols-2 lg:gap-4" },
                React.createElement("div", { className: "mb-4" },
                    React.createElement("label", { className: "block mb-1 form-label" }, t('CUSTOMER_SCAC_CODE')),
                    React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('UNIQUE_SCAC_MESSAGE')),
                    React.createElement("input", { type: "text", className: "w-full mb-4 form-field", placeholder: t('CUSTOMER_SCAC_CODE'), value: this.state.scac_code, onChange: (evt) => this.handleScacCodeChange(evt.target.value) })),
                React.createElement("div", { className: "mb-4" },
                    React.createElement("label", { className: "block mb-1 form-label" }, t('LOCATION_NAME')),
                    React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('USED_LOCATION_PAIR_MAPPING')),
                    React.createElement("input", { type: "text", className: "w-full mb-4 form-field", placeholder: t('LOCATION_PAIR_MAPPING'), value: this.state.location_name, onChange: (evt) => {
                            this.handleLocationNameChange(evt.target.value);
                        } })),
                React.createElement("div", { className: "mb-4" },
                    React.createElement("label", { className: "block mb-1 form-label" }, t('EDI_VERSION')),
                    React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('EDI_VERSION_NOTE')),
                    React.createElement(DropdownButton, { title: this.state.edi_version
                            ? t('EDI_VERSION_WITH_VALUE', {
                                value: this.state.edi_version,
                            })
                            : t('SELECT_EDI_VERSION'), onSelect: this.handleEdiVersionSelect },
                        React.createElement(MenuItem, { key: "1", eventKey: "1" }, t('EDI_VERSION_1')),
                        React.createElement(MenuItem, { key: "2", eventKey: "2" }, t('EDI_VERSION_2')),
                        React.createElement(MenuItem, { key: "3", eventKey: "3" }, t('EDI_VERSION_3')))),
                this.renderMarkets(),
                React.createElement("div", { className: "mb-4" },
                    React.createElement("label", { className: "block mb-1 form-label" }, t('TRADELENS_INTEGRATIONS')),
                    React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('SEND_OUTBOUND_EVENTS_TRADELENS')),
                    React.createElement(DropdownButton, { title: this.state.tradelens_enabled ? t('ENABLED') : t('DISABLED'), onSelect: this.handleTradelensSelect },
                        React.createElement(MenuItem, { key: "true", eventKey: "true" }, t('ENABLED')),
                        React.createElement(MenuItem, { key: "false", eventKey: "false" }, t('DISABLED')))),
                React.createElement(RenderSeriesDropdown, { selectedSeriesId: this.state.selectedSeriesId, customerType: "shipper", handleSeriesSelect: this.handleSeriesSelect }),
                React.createElement("div", { className: "mb-4" },
                    React.createElement("label", { className: "block mb-1 form-label" }, t('CUSTOMER_ACCOUNT_NUMBER')),
                    React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('CUSTOMER_INTERNAL_ACCOUNT_NUMBER')),
                    React.createElement("input", { type: "text", className: "w-full mb-4 form-field", placeholder: t('CUSTOMER_ACCOUNT_NUMBER'), value: this.state.customerAccountNumber, onChange: (evt) => this.handleCustomerAccountInput(evt.target.value) })),
                React.createElement("div", { className: "mb-4" },
                    React.createElement("label", { className: "block mb-1 form-label" }, t('PRODUCT_TYPE')),
                    React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('PRODUCT_TYPE_NOTE')),
                    React.createElement(DropdownButton, { title: this.state.productType
                            ? this.state.productType
                            : t('SELECT_PRODUCT'), onSelect: this.handleProductTypeSelect }, productTypeMenuItems))),
            React.createElement("div", { className: "mb-4" },
                React.createElement("label", { className: "block mb-1 form-label" }, t('ENABLE_EVENTS')),
                React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('SELECT_EVENTS_FOR_CUSTOMER')),
                React.createElement("div", { className: "mb-4 grid grid-cols-2 lg:grid-cols-4 gap-4" }, events)),
            React.createElement("div", { className: "mb-4" },
                React.createElement("label", { className: "block mb-1 form-label" }, t('STATUS_MAPPING')),
                React.createElement("p", { className: "mt-1 mb-2" }, t('KEY_VALUE_FOR_SHIPPERS')),
                mappings,
                React.createElement(ButtonToolbar, null,
                    React.createElement(Button, { bsStyle: "primary", onClick: () => this.handleAddStatusMap(), disabled: disabled },
                        React.createElement(Glyphicon, { glyph: "plus" }),
                        " ",
                        t('ADD_STATUS_MAP')),
                    React.createElement(Button, { style: { display: displayClear }, bsStyle: "danger", onClick: () => this.handleClearMappings(), disabled: disabled },
                        React.createElement(Glyphicon, { glyph: "remove" }),
                        " ",
                        titleize(t('CLEAR'))))),
            React.createElement("div", { className: "my-4" },
                React.createElement(Row, { style: { paddingTop: 10 } },
                    React.createElement(Col, { xs: 12 },
                        React.createElement("h4", null, t('EDI_OVERRIDES')),
                        React.createElement("p", null, t('ADDITIONAL_SCAC_CODES_FOR_EDI')),
                        this.renderEDIOverrideInput(),
                        React.createElement(ButtonToolbar, null,
                            React.createElement(Button, { bsStyle: "primary", onClick: () => this.handleAddOverrideRow(), disabled: !this.state.override_input.scac_code.length ||
                                    !this.state.override_input.enabled_events.length },
                                React.createElement(Glyphicon, { glyph: "plus" }),
                                " ",
                                t('ADD_OVERRIDE')),
                            React.createElement(Button, { style: { display: displayClear }, bsStyle: "danger", onClick: () => this.handleClearMappings(), disabled: disabled },
                                React.createElement(Glyphicon, { glyph: "remove" }),
                                " ",
                                titleize(t('CLEAR')))))),
                React.createElement(Row, null, this.renderEDIOverrides())),
            React.createElement("div", { className: "my-4" },
                React.createElement("label", { className: "block mb-1 form-label" }, t('TAGS')),
                React.createElement("p", { className: "mt-1 mb-2 text-sm" }, t('CUSTOMER_TAGS_NOTE')),
                React.createElement(TagEditor, { client: "edi-settings", object: this.props.shipper, tags: this.state.tags, suggestions: Constants.customerTagSuggestions, handleChange: (_shipper, tags) => this.handleTagUpdate(tags), hideLabel: false }))));
    }
}
EDISettings.propTypes = {
    shipper: PropTypes.object.isRequired,
};
export default withTranslation()(EDISettings);
